import { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { Phone, Mail, MapPin, Clock, Star, CheckCircle, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactPage() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service_needed: [],
    project_description: '',
    timeline: '',
    how_heard: '',
  });

  const services = [
    'AC Install & Repair',
    'Carpentry',
    'Electrical',
    'Flooring',
    'Home Renovation',
    'Painting',
    'Plumbing',
    'Roofing',
    'Sheetrock / Drywall',
    'Not Sure Yet',
  ];

  const handleServiceChange = (service, checked) => {
    setFormData(prev => ({
      ...prev,
      service_needed: checked
        ? [...prev.service_needed, service]
        : prev.service_needed.filter(s => s !== service)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/estimates`, formData);
      if (response.data.success) {
        setIsSubmitted(true);
        toast.success('Request submitted successfully!');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const faqs = [
    {
      question: 'What area do you serve?',
      answer: 'We serve the DFW Metroplex. Depending on the project, we may be able to travel further. Get in touch to discuss your location.',
    },
    {
      question: 'How long does a typical project take?',
      answer: 'Project timelines vary depending on scope. A kitchen or bathroom renovation can take a few weeks, while larger projects like full home renovations may take several months. We provide clear written timelines before starting any work.',
    },
    {
      question: 'Do you offer free quotes?',
      answer: 'No, we charge a $49.99 consultation fee. However, it\'s applied to the invoice upon booking.',
    },
    {
      question: 'Will I need permits for my project?',
      answer: 'Some projects require permits while others don\'t. We can advise on the necessary permits and handle the process for you when needed.',
    },
    {
      question: 'Do you provide a guarantee for your work?',
      answer: 'Absolutely! We stand by the quality of our craftsmanship.',
    },
    {
      question: 'Can I stay in my home while work is being done?',
      answer: 'It depends on the type of work. For smaller projects like bathrooms and kitchens, staying home may be possible. Larger renovations may require temporary accommodation. We\'ll discuss this before starting.',
    },
  ];

  if (isSubmitted) {
    return (
      <main data-testid="contact-page" className="pt-20">
        <section className="min-h-[80vh] flex items-center justify-center bg-off-white">
          <div className="max-w-lg mx-auto px-4 text-center">
            <div className="w-16 h-16 bg-copper/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-8 h-8 text-copper" />
            </div>
            <h1 className="text-3xl font-playfair font-bold text-navy">
              Got it — we'll be in touch in 48 hours.
            </h1>
            <p className="text-navy/60 font-inter mt-4">
              We've received your estimate request and will respond soon. Check your email for confirmation.
            </p>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main data-testid="contact-page" className="pt-20">
      {/* Hero */}
      <section className="bg-navy grain-overlay py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-copper text-xs font-inter uppercase tracking-widest">
              Contact
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-playfair font-bold text-white mt-4 leading-tight">
              Let's Talk About Your Project
            </h1>
            <p className="text-white/70 font-inter text-lg mt-6 leading-relaxed">
              Fill out the form and we'll respond in 48 hours. Prefer to call? We're real people who answer the phone.
            </p>
          </div>
        </div>
      </section>

      {/* Form & Contact Info */}
      <section className="py-24 bg-off-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-16">
            {/* Form */}
            <div className="lg:col-span-2">
              <form onSubmit={handleSubmit} className="space-y-8" data-testid="estimate-form">
                {/* Contact Info */}
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="font-inter text-navy">
                      Your Name *
                    </Label>
                    <Input
                      id="name"
                      data-testid="input-name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      className="h-12 border-tan/30 focus:border-copper"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="font-inter text-navy">
                      Phone Number *
                    </Label>
                    <Input
                      id="phone"
                      type="tel"
                      data-testid="input-phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      required
                      className="h-12 border-tan/30 focus:border-copper"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="font-inter text-navy">
                    Email Address *
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    data-testid="input-email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    className="h-12 border-tan/30 focus:border-copper"
                  />
                </div>

                {/* Services */}
                <div className="space-y-4">
                  <Label className="font-inter text-navy">
                    Service Needed *
                  </Label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {services.map((service) => (
                      <div key={service} className="flex items-center space-x-2">
                        <Checkbox
                          id={service}
                          data-testid={`checkbox-${service.toLowerCase().replace(/\s+/g, '-')}`}
                          checked={formData.service_needed.includes(service)}
                          onCheckedChange={(checked) => handleServiceChange(service, checked)}
                        />
                        <Label 
                          htmlFor={service} 
                          className="font-inter text-sm text-navy/80 cursor-pointer"
                        >
                          {service}
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Project Description */}
                <div className="space-y-2">
                  <Label htmlFor="description" className="font-inter text-navy">
                    Project Description *
                  </Label>
                  <Textarea
                    id="description"
                    data-testid="input-description"
                    value={formData.project_description}
                    onChange={(e) => setFormData({ ...formData, project_description: e.target.value })}
                    required
                    rows={5}
                    className="border-tan/30 focus:border-copper"
                    placeholder="Tell us about your project..."
                  />
                </div>

                {/* Timeline */}
                <div className="space-y-2">
                  <Label htmlFor="timeline" className="font-inter text-navy">
                    Timeline
                  </Label>
                  <Select
                    value={formData.timeline}
                    onValueChange={(value) => setFormData({ ...formData, timeline: value })}
                  >
                    <SelectTrigger 
                      className="h-12 border-tan/30 bg-white"
                      data-testid="select-timeline"
                    >
                      <SelectValue placeholder="When would you like to start?" />
                    </SelectTrigger>
                    <SelectContent className="bg-white z-[100]">
                      <SelectItem value="asap">ASAP</SelectItem>
                      <SelectItem value="1-month">Within 1 month</SelectItem>
                      <SelectItem value="1-3-months">1-3 months</SelectItem>
                      <SelectItem value="exploring">Just exploring</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* How Heard */}
                <div className="space-y-2">
                  <Label htmlFor="how_heard" className="font-inter text-navy">
                    How Did You Hear About Us?
                  </Label>
                  <Select
                    value={formData.how_heard}
                    onValueChange={(value) => setFormData({ ...formData, how_heard: value })}
                  >
                    <SelectTrigger 
                      className="h-12 border-tan/30 bg-white"
                      data-testid="select-how-heard"
                    >
                      <SelectValue placeholder="Select an option" />
                    </SelectTrigger>
                    <SelectContent className="bg-white z-[100]">
                      <SelectItem value="google">Google</SelectItem>
                      <SelectItem value="nextdoor">Nextdoor</SelectItem>
                      <SelectItem value="referral">Referral</SelectItem>
                      <SelectItem value="social">Social Media</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Submit */}
                <div className="pt-4">
                  <Button
                    type="submit"
                    data-testid="submit-btn"
                    disabled={isSubmitting || formData.service_needed.length === 0}
                    className="w-full md:w-auto bg-copper hover:bg-copper/90 text-white px-12 py-6 text-xs font-bold uppercase tracking-widest"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      'Book My Estimate Now - $49.99'
                    )}
                  </Button>
                  <p className="text-navy/50 font-inter text-sm mt-4">
                    We'll respond in 48 hours. (Deposit will be applied to invoice after booking)
                  </p>
                </div>
              </form>
            </div>

            {/* Contact Info Sidebar */}
            <div className="space-y-8">
              <div className="bg-white p-8 border border-tan/20">
                <h3 className="font-playfair font-bold text-navy text-xl mb-6">
                  Contact Info
                </h3>
                <ul className="space-y-6">
                  <li className="flex items-start space-x-4">
                    <Phone className="w-5 h-5 text-copper mt-1" />
                    <div>
                      <p className="font-inter font-medium text-navy">Phone</p>
                      <a 
                        href="tel:+19453584803" 
                        className="text-navy/70 font-inter hover:text-copper transition-colors"
                      >
                        (945) 358-4803
                      </a>
                    </div>
                  </li>
                  <li className="flex items-start space-x-4">
                    <Mail className="w-5 h-5 text-copper mt-1" />
                    <div>
                      <p className="font-inter font-medium text-navy">Email</p>
                      <a 
                        href="mailto:info@cabenterprisesoftexas.com" 
                        className="text-navy/70 font-inter hover:text-copper transition-colors"
                      >
                        info@cabenterprisesoftexas.com
                      </a>
                    </div>
                  </li>
                  <li className="flex items-start space-x-4">
                    <MapPin className="w-5 h-5 text-copper mt-1" />
                    <div>
                      <p className="font-inter font-medium text-navy">Service Area</p>
                      <p className="text-navy/70 font-inter text-sm">
                        DFW Metroplex
                      </p>
                    </div>
                  </li>
                  <li className="flex items-start space-x-4">
                    <Clock className="w-5 h-5 text-copper mt-1" />
                    <div>
                      <p className="font-inter font-medium text-navy">Hours</p>
                      <p className="text-navy/70 font-inter text-sm">Mon-Fri: 8:30am - 6pm</p>
                      <p className="text-copper font-inter text-sm">Emergency services on weekends</p>
                    </div>
                  </li>
                </ul>
              </div>

              {/* Trust */}
            </div>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-copper text-xs font-inter uppercase tracking-widest">
              FAQs
            </span>
            <h2 className="text-4xl font-playfair font-bold text-navy mt-4">
              Answering your questions
            </h2>
          </div>

          <Accordion type="single" collapsible className="space-y-4" data-testid="faq-accordion">
            {faqs.map((faq, index) => (
              <AccordionItem 
                key={index} 
                value={`faq-${index}`}
                className="border border-tan/20 px-6"
              >
                <AccordionTrigger 
                  className="py-6 hover:no-underline"
                  data-testid={`faq-trigger-${index}`}
                >
                  <span className="font-playfair font-semibold text-navy text-left">
                    {faq.question}
                  </span>
                </AccordionTrigger>
                <AccordionContent className="pb-6">
                  <p className="text-navy/70 font-inter leading-relaxed">
                    {faq.answer}
                  </p>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>
    </main>
  );
}
